/**
 * Cart drawer GWP sync — mirrors checkout discount-gift-extension logic.
 * Config: shop.metafields.custom.gwp_settings (embedded via fd-cart-gwp-config.liquid)
 */
(function () {
  'use strict';

  const LOG = '[CartGwp]';
  const DEFAULT_COLLECTION_GWP_METAFIELD_NAMESPACE = 'custom';
  const DEFAULT_COLLECTION_GWP_METAFIELD_KEY = 'gwp_gift';
  const COLLECTION_GWP_ATTRIBUTE_KEY = '_collection_gwp';
  const COLLECTION_GWP_ATTRIBUTE_VALUE = '1';

  const normalizeId = (id) => {
    if (!id) return null;
    if (typeof window.normalizeNumericId === 'function') {
      return window.normalizeNumericId(id);
    }
    const str = String(id).trim();
    if (str.startsWith('gid://')) {
      const parts = str.split('/');
      return parts[parts.length - 1] || null;
    }
    return str;
  };

  function parseMaxGiftQuantity(value) {
    if (typeof value === 'number' && value >= 1) return Math.floor(value);
    return 1;
  }

  const toGid = (id, resource = 'ProductVariant') => {
    if (!id) return null;
    const str = String(id).trim();
    if (str.startsWith('gid://')) return str;
    if (typeof window.toGID === 'function') {
      return window.toGID(str, resource);
    }
    return `gid://shopify/${resource}/${str}`;
  };

  function parseCollectionGwpEntry(entry, fallbackGiftVariantId, defaultThreshold) {
    if (typeof entry === 'string') {
      return {
        handle: entry,
        giftMetafieldNamespace: DEFAULT_COLLECTION_GWP_METAFIELD_NAMESPACE,
        giftMetafieldKey: DEFAULT_COLLECTION_GWP_METAFIELD_KEY,
        fallbackGiftVariantId,
        threshold: defaultThreshold,
      };
    }

    if (typeof entry === 'object' && entry !== null && typeof entry.handle === 'string') {
      return {
        handle: entry.handle,
        giftMetafieldNamespace: entry.giftMetafieldNamespace || DEFAULT_COLLECTION_GWP_METAFIELD_NAMESPACE,
        giftMetafieldKey: entry.giftMetafieldKey || DEFAULT_COLLECTION_GWP_METAFIELD_KEY,
        fallbackGiftVariantId,
        threshold: typeof entry.threshold === 'number' ? entry.threshold : defaultThreshold,
      };
    }

    return null;
  }

  function parseCollectionGwpConfigs(item) {
    if (!item || !item.collectionGwp) return [];

    const fallbackGiftVariantId = typeof item.giftVariant === 'string' ? item.giftVariant : null;
    const defaultThreshold = typeof item.threshold === 'number' ? item.threshold : null;

    if (Array.isArray(item.collectionGwp)) {
      return item.collectionGwp
        .map((entry) => parseCollectionGwpEntry(entry, fallbackGiftVariantId, defaultThreshold))
        .filter(Boolean);
    }

    const single = parseCollectionGwpEntry(item.collectionGwp, fallbackGiftVariantId, defaultThreshold);
    return single ? [single] : [];
  }

  function normalizeCollectionCheckMatch(value) {
    const raw = String(value || 'any')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    if (raw === 'all' || raw === 'all_of' || raw === 'every_paid') return 'all';
    if (raw === 'all_present' || raw === 'and' || raw === 'each') return 'all_present';
    return 'any';
  }

  function parseGiftQuantity(value) {
    if (typeof value === 'number' && value >= 1) return Math.floor(value);
    return 0;
  }

  function parseGiftCopy(entry) {
    if (!entry || typeof entry !== 'object') return { giftLabel: '', giftDescription: '' };
    const giftLabel = typeof entry.giftLabel === 'string' ? entry.giftLabel.trim() : '';
    const rawDesc = entry.giftDescription || entry.description;
    const giftDescription = typeof rawDesc === 'string' ? rawDesc.trim() : '';
    return { giftLabel, giftDescription };
  }

  function parseCollectionCheckGift(entry, fallbackQty, fallbackCopy) {
    const qty = parseGiftQuantity(
      (entry && typeof entry === 'object'
        ? entry.quantity || entry.qty || entry.maxGiftQuantity
        : null) || fallbackQty
    ) || 1;
    const inherited = fallbackCopy || { giftLabel: '', giftDescription: '' };
    if (typeof entry === 'string' && entry.trim()) {
      return {
        giftVariantId: entry.trim(),
        giftLabel: inherited.giftLabel || 'Free Gift',
        giftDescription: inherited.giftDescription || '',
        quantity: qty,
      };
    }
    if (!entry || typeof entry !== 'object') return null;
    const giftVariantId = entry.giftVariantId || entry.giftVariant;
    if (typeof giftVariantId !== 'string' || !giftVariantId.trim()) return null;
    const own = parseGiftCopy(entry);
    return {
      giftVariantId: giftVariantId.trim(),
      giftLabel: own.giftLabel || inherited.giftLabel || 'Free Gift',
      giftDescription: own.giftDescription || inherited.giftDescription || '',
      quantity: qty,
    };
  }

  function parseCollectionChecks(item) {
    const raw = Array.isArray(item?.collectionChecks)
      ? item.collectionChecks
      : Array.isArray(item?.collection_checks)
        ? item.collection_checks
        : [];
    if (!raw.length) return [];

    return raw
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const collections = []
          .concat(entry.collections || entry.handles || [])
          .map((handle) => (typeof handle === 'string' ? handle.trim() : ''))
          .filter(Boolean);
        if (!collections.length) return null;

        const checkQty = parseGiftQuantity(entry.quantity || entry.qty || entry.maxGiftQuantity) || 1;
        const checkCopy = parseGiftCopy(entry);
        const gifts = [];
        const seen = new Set();
        const pushGift = (gift, copy) => {
          const parsed = parseCollectionCheckGift(gift, checkQty, copy);
          if (!parsed) return;
          const key = normalizeId(parsed.giftVariantId);
          if (!key || seen.has(key)) return;
          seen.add(key);
          gifts.push(parsed);
        };

        pushGift(entry.giftVariant || entry.giftVariantId, checkCopy);
        if (Array.isArray(entry.gifts)) {
          entry.gifts.forEach((gift) => pushGift(gift, typeof gift === 'string' ? checkCopy : null));
        }

        const swap = [];
        if (Array.isArray(entry.swap)) {
          entry.swap.forEach((gift) => {
            const parsed = parseCollectionCheckGift(gift, 1);
            if (!parsed) return;
            const key = normalizeId(parsed.giftVariantId);
            if (!key || seen.has(key)) return;
            seen.add(key);
            swap.push(parsed);
          });
        }

        if (!gifts.length) return null;

        const productIds = []
          .concat(entry.productIds || entry.product_ids || [])
          .map((id) => normalizeId(id))
          .filter(Boolean);

        return {
          collections,
          productIds,
          match: normalizeCollectionCheckMatch(entry.match),
          gifts,
          swap,
          // Presence-only by default. Parent $75 giftTiers must not leak onto collection gifts.
          threshold: typeof entry.threshold === 'number' ? entry.threshold : 0,
          // null = auto: match:"all" (hydration-only) stops; later disjoint checks still stack.
          stop: item.collectionChecksMode === 'exclusive' ? true : (typeof entry.stop === 'boolean' ? entry.stop : null),
          exclusive: item.collectionChecksMode === 'exclusive' || entry.selectionMode === 'exclusive',
          giftLabel: checkCopy.giftLabel,
          giftDescription: checkCopy.giftDescription,
          requireUserChoice:
            entry.requireUserChoice === true ? true : entry.requireUserChoice === false ? false : null,
        };
      })
      .filter(Boolean);
  }

  function collectionCheckRequiresUserChoice(setting, check) {
    if (typeof check?.requireUserChoice === 'boolean') return check.requireUserChoice;
    return setting?.requireUserChoice === true;
  }

  function collectionCheckMeetsSpend(check, netSales) {
    const threshold = typeof check?.threshold === 'number' ? check.threshold : 0;
    return !(threshold > 0 && netSales < threshold);
  }

  function cartHasGiftVariant(cart, giftVariantId) {
    return (cart.items || []).some(
      (line) => normalizeId(line.variant_id) === normalizeId(giftVariantId)
    );
  }

  // Keep default slot order. One shared alt (bottle) fills the first missing slot only.
  function resolveCollectionCheckGifts(check, cart, claimedAltIds) {
    const defaults = check.gifts || [];
    const alts = check.swap || [];
    const claimed = claimedAltIds || new Set();
    if (!defaults.length) return defaults;

    const availableAlt = alts.find((gift) => {
      const id = normalizeId(gift.giftVariantId);
      return id && cartHasGiftVariant(cart, gift.giftVariantId) && !claimed.has(id);
    });

    let altUsed = false;
    return defaults.map((gift) => {
      if (cartHasGiftVariant(cart, gift.giftVariantId)) return gift;
      if (availableAlt && !altUsed) {
        altUsed = true;
        claimed.add(normalizeId(availableAlt.giftVariantId));
        return availableAlt;
      }
      return gift;
    });
  }

  function toCollectionCheckUiGift(gift) {
    return {
      giftVariantId: toGid(gift.giftVariantId),
      giftLabel: gift.giftLabel || 'Free Gift',
      giftDescription: gift.giftDescription || '',
      quantity: gift.quantity >= 1 ? gift.quantity : 1,
      unlocked: true,
    };
  }

  // An exclusive choice is one offer, even when it contains several fulfillment SKUs.
  function exclusiveCollectionChoices(check) {
    const defaults = (check.gifts || []).map(toCollectionCheckUiGift);
    if (!defaults.length) return [];
    const grouped = Object.assign({}, defaults[0], {
      giftLabel: check.giftLabel || defaults[0].giftLabel,
      giftDescription: check.giftDescription || defaults[0].giftDescription,
      quantity: defaults.length > 1 ? 1 : defaults[0].quantity,
      components: defaults,
    });
    return [grouped].concat((check.swap || []).map((gift) => {
      const choice = toCollectionCheckUiGift(gift);
      return Object.assign({}, choice, { components: [choice] });
    }));
  }

  function isExclusiveGiftLine(line, discountCode) {
    const props = line.properties || Object.fromEntries((line.attributes || []).map((a) => [a.key, a.value]));
    const protectedGift = [props._bundle_gift, props._box_picker_gift, props._welcome_kit_gift]
      .some((value) => value === true || value === 'true') || props._upsell_module === 'build_box';
    return !protectedGift && props._upsell_module === 'checkout_gwp' &&
      String(props._discount_code_source || '').toLowerCase() === String(discountCode || '').toLowerCase();
  }

  function exclusiveCollectionSelection(check, lines, discountCode, overrideId) {
    const choices = exclusiveCollectionChoices(check);
    const hasGift = (gift) => (lines || []).some((line) =>
      isExclusiveGiftLine(line, discountCode) &&
      normalizeId(line.variant_id || line.merchandise?.id) === normalizeId(gift.giftVariantId)
    );
    // A selected alternative wins over stale components from an interrupted swap.
    const choice = choices.find((gift) => normalizeId(gift.giftVariantId) === normalizeId(overrideId)) ||
      choices.slice(1).find((gift) => gift.components.some(hasGift)) || choices[0];
    return { choices, choice, selected: !!choice && choice.components.some(hasGift) };
  }

  function exclusiveCollectionPool(check, lines, discountCode, requireUserChoice, overrideId) {
    const { choices, choice, selected } = exclusiveCollectionSelection(check, lines, discountCode, overrideId);
    if (!choice) return null;
    return {
      type: 'collectionCheck',
      key: `${String(discountCode || '').toLowerCase()}::${(check.collections || []).join('+')}::0`,
      discountCode,
      exclusive: true,
      gifts: [choice],
      swapChoices: choices,
      selectedVariantId: requireUserChoice && !selected ? '' : choice.giftVariantId,
      requireUserChoice: !!requireUserChoice,
    };
  }

  function collectionChoiceComponents(pool, variantId) {
    const choice = (pool?.swapChoices || []).find((gift) =>
      normalizeId(gift.giftVariantId) === normalizeId(variantId)
    );
    return choice ? (choice.components || [choice]) : [];
  }

  function collectionCheckGiftQuantity(eligibleGifts, variantId) {
    let max = 1;
    (eligibleGifts || []).forEach((gift) => {
      if (normalizeId(gift.giftVariantId) !== normalizeId(variantId)) return;
      const qty = gift.quantity >= 1 ? gift.quantity : 1;
      if (qty > max) max = qty;
    });
    return max;
  }

  function quantityForPoolGift(pool, variantId) {
    const lists = [pool && pool.gifts, pool && pool.swapChoices].filter(Boolean);
    for (let i = 0; i < lists.length; i++) {
      const hit = lists[i].find(
        (gift) => normalizeId(gift.giftVariantId) === normalizeId(variantId)
      );
      if (hit && hit.quantity >= 1) return hit.quantity;
    }
    return 1;
  }

  function collectionCheckDisplayPool(check, cart, discountCode, claimedAltIds, requireUserChoice) {
    if (check.exclusive) return exclusiveCollectionPool(check, cart.items, discountCode, requireUserChoice);
    const defaults = check.gifts || [];
    const alts = check.swap || [];
    const resolved = resolveCollectionCheckGifts(check, cart, claimedAltIds);
    if (!resolved.length) return null;

    const swapChoices = [];
    const seen = new Set();
    const choiceSource = alts.length ? defaults.concat(alts) : defaults;
    choiceSource.forEach((gift) => {
      const key = normalizeId(gift.giftVariantId);
      if (!key || seen.has(key)) return;
      seen.add(key);
      swapChoices.push(gift);
    });

    const selectedInCart = resolved.find((gift) => cartHasGiftVariant(cart, gift.giftVariantId));
    const selected = selectedInCart || resolved.find((gift) =>
      alts.some((alt) => normalizeId(alt.giftVariantId) === normalizeId(gift.giftVariantId))
    ) || resolved[0];
    const pickFirst = requireUserChoice === true;
    const selectedVariantId = pickFirst && !selectedInCart ? '' : toGid(selected.giftVariantId);

    return {
      type: 'collectionCheck',
      key: `${String(discountCode || '').toLowerCase()}::${(check.collections || []).join('+')}`,
      discountCode,
      gifts: resolved.map(toCollectionCheckUiGift),
      swapChoices: swapChoices.map(toCollectionCheckUiGift),
      defaultGiftIds: defaults.map((gift) => toGid(gift.giftVariantId)),
      swapGiftIds: alts.map((gift) => toGid(gift.giftVariantId)),
      selectedVariantId,
      requireUserChoice: pickFirst,
    };
  }

  function collectionCheckSlotPools(pool, cart) {
    if (!pool) return [];
    if (pool.exclusive) return [pool];
    const altIds = {};
    (pool.swapGiftIds || []).forEach((id) => {
      const key = normalizeId(id);
      if (key) altIds[key] = true;
    });
    const pickFirst = pool.requireUserChoice === true;
    return (pool.gifts || []).map((gift, index) => {
      const defaultId = (pool.defaultGiftIds && pool.defaultGiftIds[index]) || gift.giftVariantId;
      const inCart = cartHasGiftVariant(cart, gift.giftVariantId);
      const swapChoices = (pool.swapChoices || []).filter((choice) => {
        const id = normalizeId(choice.giftVariantId);
        return id === normalizeId(defaultId) || altIds[id];
      });
      return Object.assign({}, pool, {
        key: pool.key + '::' + index,
        gifts: [gift],
        swapChoices: swapChoices.length ? swapChoices : [gift],
        selectedVariantId: pickFirst && !inCart ? '' : toGid(gift.giftVariantId),
        slotIndex: index,
      });
    });
  }

  function collectionCheckSwapPool(check, cart, discountCode) {
    const pool = collectionCheckDisplayPool(check, cart, discountCode);
    if (!pool || (pool.swapChoices || []).length < 2) return null;
    return Object.assign({}, pool, { gifts: pool.swapChoices });
  }

  function collectionSetsOverlap(left, right) {
    const other = new Set(right || []);
    return (left || []).some((handle) => other.has(handle));
  }

  function collectionCheckIsCovered(check, matchedSets) {
    const cols = check.collections || [];
    if (!cols.length || !matchedSets.length) return false;
    return matchedSets.some((prev) => collectionSetsOverlap(cols, prev));
  }

  function collectionCheckShouldStop(check) {
    if (check.stop === true) return true;
    if (check.stop === false) return false;
    return check.match === 'all';
  }

  function forEachMatchingCollectionCheck(setting, cart, allGiftVariantIds, collectionProducts, visit) {
    const checks = setting.collectionChecks || [];
    if (!checks.length) return;

    const matchingCart = checks.some((check) => check.exclusive)
      ? Object.assign({}, cart, { items: (cart.items || []).filter((line) => !isExclusiveGiftLine(line, setting.appliedDiscountCode)) })
      : cart;
    const paidProductIds = paidCartProductIds(matchingCart, allGiftVariantIds);
    const matchedSets = [];

    for (const check of checks) {
      if (!collectionCheckMeetsSpend(check, setting.netSales)) {
        if (check.stop === true && !check.exclusive) break;
        continue;
      }

      if (!cartMatchesCollectionCheck(check, paidProductIds, collectionProducts)) {
        continue;
      }

      if (collectionCheckIsCovered(check, matchedSets)) {
        continue;
      }

      visit(check);
      matchedSets.push(check.collections || []);
      if (collectionCheckShouldStop(check)) break;
    }
  }

  function collectionHandlesFromSettings(discountSettings) {
    const handles = new Set();
    (discountSettings || []).forEach((setting) => {
      (setting.collectionGwpConfigs || []).forEach((config) => {
        if (config.handle) handles.add(config.handle);
      });
      (setting.collectionChecks || []).forEach((check) => {
        (check.collections || []).forEach((handle) => handles.add(handle));
      });
    });
    return [...handles];
  }

  function parseSellingPlanGifts(item) {
    const raw = Array.isArray(item?.sellingPlanGifts)
      ? item.sellingPlanGifts
      : Array.isArray(item?.selling_plan_gifts)
        ? item.selling_plan_gifts
        : null;
    if (!raw) return [];

    const fallbackThreshold = typeof item.threshold === 'number' ? item.threshold : 0;

    return raw
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const giftVariantId = entry.giftVariant || entry.giftVariantId;
        if (typeof giftVariantId !== 'string' || !giftVariantId.trim()) return null;

        const sellingPlanId = String(
          entry.sellingPlanId ?? entry.selling_plan_id ?? ''
        ).trim();
        const sellingPlanNameSubstring = String(
          entry.sellingPlanNameSubstring ??
            entry.selling_plan_name_substring ??
            entry.selling_plan_name_sub_string ??
            ''
        ).trim();

        if (!sellingPlanId && !sellingPlanNameSubstring) return null;

        return {
          sellingPlanId,
          sellingPlanNameSubstring,
          giftVariantId,
          threshold: typeof entry.threshold === 'number' ? entry.threshold : fallbackThreshold,
          maxGiftQuantity: parseMaxGiftQuantity(entry.maxGiftQuantity),
        };
      })
      .filter(Boolean);
  }

  function getCollectionGwpCacheKey(config) {
    return `${config.handle}:${config.giftMetafieldNamespace}:${config.giftMetafieldKey}`;
  }

  function getGiftUnlockThreshold(giftVariantId, thresholdGifts, fallbackThreshold) {
    const targetId = normalizeId(giftVariantId);
    const tier = (thresholdGifts || []).find(
      (t) => normalizeId(t.giftVariantId) === targetId
    );
    if (typeof tier?.threshold === 'number') return tier.threshold;
    if (typeof fallbackThreshold === 'number') return fallbackThreshold;
    return 0;
  }

  /** Choice pools = 2+ selector gifts that unlock at the SAME threshold (pick one). */
  function getSelectorChoicePools(setting) {
    const byThreshold = new Map();
    (setting.giftOptionsForSelectorDropdown || []).forEach((gift) => {
      const threshold =
        typeof gift.unlockThreshold === 'number' ? gift.unlockThreshold : 0;
      if (!byThreshold.has(threshold)) byThreshold.set(threshold, []);
      byThreshold.get(threshold).push(gift);
    });
    return [...byThreshold.entries()]
      .filter(([, gifts]) => gifts.length >= 2)
      .map(([threshold, gifts]) => ({ threshold, gifts }))
      .sort((a, b) => a.threshold - b.threshold);
  }

  function getActiveSelectorChoicePools(setting) {
    const netSales = setting.netSales ?? 0;
    return getSelectorChoicePools(setting).filter((pool) => netSales >= pool.threshold);
  }

  function isGiftInSelectorChoicePool(setting, giftVariantId) {
    const targetId = normalizeId(giftVariantId);
    return getSelectorChoicePools(setting).some((pool) =>
      pool.gifts.some((g) => normalizeId(g.giftVariantId) === targetId)
    );
  }

  function parseDiscountSettings(rawEntries) {
    const parseThresholdGift = (entry) => {
      const giftVariantId = entry?.giftVariantId || entry?.giftVariant;
      if (entry && typeof giftVariantId === 'string' && typeof entry.threshold === 'number') {
        return { giftVariantId, threshold: entry.threshold };
      }
      return null;
    };

    const dedupeThresholdGifts = (gifts) => {
      const byVariant = new Map();
      gifts.forEach((gift) => {
        const existing = byVariant.get(gift.giftVariantId);
        if (!existing || gift.threshold > existing.threshold) {
          byVariant.set(gift.giftVariantId, gift);
        }
      });
      return [...byVariant.values()];
    };

    if (!Array.isArray(rawEntries)) return [];

    return rawEntries
      .map((item) => {
        if (
          !item ||
          typeof item.discountCode !== 'string' ||
          typeof item.discountPercentage !== 'number'
        ) {
          return null;
        }

        const hasGiftTiers = Array.isArray(item.giftTiers) && item.giftTiers.length > 0;
        const collectionGwpConfigs = parseCollectionGwpConfigs(item);
        const collectionChecks = parseCollectionChecks(item);
        const hasCollectionGwp = collectionGwpConfigs.length > 0 || collectionChecks.length > 0;
        const sellingPlanGifts = parseSellingPlanGifts(item);
        const hasSellingPlanGifts = sellingPlanGifts.length > 0;

        let giftOptionsForSelectorDropdown = [];
        if (Array.isArray(item.giftOptionsForSelectorDropdown) && item.giftOptionsForSelectorDropdown.length > 0) {
          giftOptionsForSelectorDropdown = item.giftOptionsForSelectorDropdown.filter(
            (g) => g && typeof g.giftVariantId === 'string' && typeof g.giftLabel === 'string'
          );
        } else if (Array.isArray(item.gifts) && item.gifts.length > 0) {
          giftOptionsForSelectorDropdown = item.gifts.filter(
            (g) => g && typeof g.giftVariantId === 'string' && typeof g.giftLabel === 'string'
          );
        } else if (typeof item.giftVariant === 'string' && !hasGiftTiers && !hasCollectionGwp) {
          giftOptionsForSelectorDropdown = [{ giftVariantId: item.giftVariant, giftLabel: 'Free Gift' }];
        }

        let giftOptionsForThreshold = [];
        if (Array.isArray(item.giftOptionsForThreshold) && item.giftOptionsForThreshold.length > 0) {
          giftOptionsForThreshold = item.giftOptionsForThreshold.map(parseThresholdGift).filter(Boolean);
        }

        if (hasGiftTiers) {
          if (typeof item.giftVariant === 'string' && typeof item.threshold === 'number') {
            giftOptionsForThreshold.push({
              giftVariantId: item.giftVariant,
              threshold: item.threshold,
            });
          }
          giftOptionsForThreshold.push(...item.giftTiers.map(parseThresholdGift).filter(Boolean));
        }

        giftOptionsForThreshold = dedupeThresholdGifts(giftOptionsForThreshold);

        giftOptionsForSelectorDropdown = giftOptionsForSelectorDropdown.map((g) => ({
          ...g,
          unlockThreshold: getGiftUnlockThreshold(
            g.giftVariantId,
            giftOptionsForThreshold,
            item.threshold
          ),
        }));

        const hasDropdownGifts = giftOptionsForSelectorDropdown.length > 0;
        const hasThresholdGifts = giftOptionsForThreshold.length > 0;

        if (!hasDropdownGifts && !hasThresholdGifts && !hasCollectionGwp && !hasSellingPlanGifts) {
          return null;
        }

        return {
          discountCode: item.discountCode,
          discountPercentage: item.discountPercentage,
          threshold: item.threshold,
          giftOptionsForSelectorDropdown,
          giftOptionsForThreshold,
          collectionGwpConfigs,
          collectionChecks,
          sellingPlanGifts,
          isMultiGift: giftOptionsForSelectorDropdown.length > 1,
          hasDropdownGifts: giftOptionsForSelectorDropdown.length > 0,
          needsAtLeastOneSubscription: item.needsAtLeastOneSubscription === true,
          requireUserChoice: item.requireUserChoice === true,
          exclusiveCollectionChecks: item.collectionChecksMode === 'exclusive',
        };
      })
      .filter(Boolean);
  }

  function loadConfig() {
    const settingsEl = document.getElementById('fd-cart-gwp-settings');
    const cacheEl = document.getElementById('fd-cart-gwp-collection-cache');
    const productsEl = document.getElementById('fd-cart-gwp-collection-products');
    const runtime = window.__fdCartGwpConfig || {};

    let rawSettings = [];
    let collectionCache = {};
    let collectionProducts = {};

    try {
      if (settingsEl && settingsEl.textContent) {
        rawSettings = JSON.parse(settingsEl.textContent);
      }
    } catch (e) {
      console.warn(LOG, 'Failed to parse gwp_settings JSON', e);
    }

    try {
      if (cacheEl && cacheEl.textContent) {
        collectionCache = JSON.parse(cacheEl.textContent);
      }
    } catch (e) {
      console.warn(LOG, 'Failed to parse collection cache JSON', e);
    }

    try {
      if (productsEl && productsEl.textContent) {
        collectionProducts = JSON.parse(productsEl.textContent);
      }
    } catch (e) {
      console.warn(LOG, 'Failed to parse collection product cache JSON', e);
    }

    Object.keys(collectionCache || {}).forEach((key) => {
      const entry = collectionCache[key];
      if (entry?.handle && Array.isArray(entry.productIds) && !collectionProducts[entry.handle]) {
        collectionProducts[entry.handle] = entry.productIds;
      }
    });

    return {
      discountSettings: parseDiscountSettings(rawSettings),
      collectionCache,
      collectionProducts,
      requireSubscriptionForGwp: runtime.requireSubscriptionForGwp === true,
      debounceDelay: Number(runtime.debounceDelay) || 500,
      globalCouponCode: typeof runtime.globalCouponCode === 'string' ? runtime.globalCouponCode.trim() : '',
    };
  }

  function cartHasSubscription(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some((item) => {
      if (!item) return false;
      const props = item.properties || {};
      if (props._add_on === true || props._add_on === 'true') return false;
      return !!(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
    });
  }

  function lineMatchesSellingPlanGift(item, rule) {
    if (!item || !rule) return false;
    const props = getLineProps(item);
    if (props._add_on === true || props._add_on === 'true') return false;
    const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (!plan) return false;

    if (rule.sellingPlanId) {
      return normalizeId(plan.id) === normalizeId(rule.sellingPlanId);
    }

    const needle = String(rule.sellingPlanNameSubstring || '').trim().toLowerCase();
    if (!needle) return false;
    return String(plan.name || '').toLowerCase().includes(needle);
  }

  function maxSellingPlanGiftQuantity(eligibleGifts, variantId) {
    const targetId = normalizeId(variantId);
    let max = 1;
    (eligibleGifts || []).forEach((gift) => {
      if (normalizeId(gift.giftVariantId) !== targetId) return;
      const qty = gift.maxGiftQuantity >= 1 ? gift.maxGiftQuantity : 1;
      if (qty > max) max = qty;
    });
    return max;
  }

  function cartMatchesSellingPlanGift(cart, rule, giftVariantIds) {
    const giftIdSet = new Set(giftVariantIds || []);
    return (cart.items || []).some((item) => {
      if (!item) return false;
      if (giftIdSet.has(normalizeId(item.variant_id))) return false;
      return lineMatchesSellingPlanGift(item, rule);
    });
  }

  function sellingPlanLines(cart, giftVariantIds) {
    const giftIdSet = new Set(giftVariantIds || []);
    return (cart.items || [])
      .filter((item) => item && !giftIdSet.has(normalizeId(item.variant_id)))
      .map((item) => {
        const props = getLineProps(item);
        const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
        return {
          key: item.key,
          title: item.product_title || item.title || null,
          variantId: normalizeId(item.variant_id),
          addOn: props._add_on === true || props._add_on === 'true',
          sellingPlanId: plan ? normalizeId(plan.id) : null,
          sellingPlanName: plan ? plan.name : null,
        };
      });
  }

  function getLineProps(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  function isProtectedNonGwpGift(item) {
    const props = getLineProps(item);
    const truthy = function (v) {
      return v === true || v === 'true';
    };
    return (
      truthy(props._bundle_gift) ||
      truthy(props._box_picker_gift) ||
      truthy(props._welcome_kit_gift) ||
      props._upsell_module === 'build_box'
    );
  }

  function resolveRuntimeGlobalCoupon(configGlobal) {
    var offer = window.FDOffer && window.FDOffer.active;
    if (offer && offer.replacesGlobal) return '';
    return typeof configGlobal === 'string' ? configGlobal.trim() : '';
  }

  function cartHasPaidMerchandise(cart, giftVariantIds) {
    const ids = giftVariantIds || [];
    return (cart.items || []).some((item) => {
      if (!item) return false;
      if (ids.includes(normalizeId(item.variant_id))) return false;
      if (isProtectedNonGwpGift(item)) return false;
      const props = getLineProps(item);
      if (props._upsell_module === 'checkout_gwp') return false;
      const productType = String(item.product_type || '').toLowerCase();
      if (productType === 'gift') return false;
      const truthy = (value) => value === true || value === 'true';
      if (
        truthy(props._free_gift) ||
        truthy(props._welcome_kit_gift) ||
        truthy(props._quantity_welcome_gift) ||
        truthy(props._bundle_gift) ||
        truthy(props._box_picker_gift)
      ) {
        return false;
      }
      return true;
    });
  }

  function emptyPaidCartGwpPlan(cart, giftVariantIds) {
    const toRemove = [];
    (cart.items || []).forEach((item) => {
      if (!item || isProtectedNonGwpGift(item)) return;
      const isGiftSku = (giftVariantIds || []).includes(normalizeId(item.variant_id));
      const props = getLineProps(item);
      if (isGiftSku || props._upsell_module === 'checkout_gwp') {
        toRemove.push({ key: item.key, quantity: item.quantity });
      }
    });
    return {
      toRemove,
      toFixQty: [],
      toAdd: [],
      planKey: toRemove.length ? 'empty-paid-gwp-cleanup' : 'idle',
    };
  }

  function getAppliedDiscounts(cart, globalCouponCode) {
    const discounts = [];
    const seen = new Set();

    const pushDiscount = (identifier, source) => {
      const trimmed = String(identifier || '').trim();
      if (!trimmed) return;
      const key = `${source}:${trimmed.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      discounts.push({ identifier: trimmed, source });
    };

    if (Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach((entry) => {
        const code = typeof entry === 'string' ? entry : entry?.code;
        if (code) pushDiscount(code, 'code');
      });
    }

    if (Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach((app) => {
        if (app && app.title) pushDiscount(app.title, 'allocation');
        if (app && app.code) pushDiscount(app.code, 'code');
      });
    }

    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        if (discount && discount.title) pushDiscount(discount.title, 'allocation');
      });

      const props = getLineProps(item);
      if (props._otp_discount_code) pushDiscount(props._otp_discount_code, 'code');
      if (props._sub_discount_code) pushDiscount(props._sub_discount_code, 'code');
    });

    if (globalCouponCode) {
      pushDiscount(globalCouponCode, 'code');
    }

    return discounts;
  }

  function getAllGiftVariantIds(discountSettings, collectionCache) {
    const ids = new Set();

    discountSettings.forEach((setting) => {
      setting.giftOptionsForSelectorDropdown.forEach((g) => ids.add(normalizeId(g.giftVariantId)));
      setting.giftOptionsForThreshold.forEach((g) => ids.add(normalizeId(g.giftVariantId)));
      (setting.collectionGwpConfigs || []).forEach((config) => {
        const cache = collectionCache[getCollectionGwpCacheKey(config)];
        const resolved = cache?.giftVariantId || config.fallbackGiftVariantId;
        if (resolved) ids.add(normalizeId(resolved));
      });
      (setting.collectionChecks || []).forEach((check) => {
        (check.gifts || []).forEach((gift) => ids.add(normalizeId(gift.giftVariantId)));
        (check.swap || []).forEach((gift) => ids.add(normalizeId(gift.giftVariantId)));
      });
      (setting.sellingPlanGifts || []).forEach((rule) => {
        if (rule.giftVariantId) ids.add(normalizeId(rule.giftVariantId));
      });
    });

    return [...ids].filter(Boolean);
  }

  function netMerchandiseSales(cart, giftVariantIds) {
    // Match gift progress bar: theme line-item discounts (e.g. PEACHY) are not
    // reflected in cart.js final_line_price — use the shared discounted total.
    if (typeof window.calculateGiftProgressTotals === 'function') {
      const totals = window.calculateGiftProgressTotals(cart);
      if (totals && Number.isFinite(totals.salePriceTotal)) {
        return totals.salePriceTotal / 100;
      }
    }

    const giftIdSet = new Set(giftVariantIds);
    return (cart.items || []).reduce((sum, item) => {
      if (!item) return sum;
      const variantId = normalizeId(item.variant_id);
      if (giftIdSet.has(variantId)) return sum;
      const cents = Number.isFinite(item.final_line_price) ? item.final_line_price : item.line_price;
      return sum + (Number(cents) || 0) / 100;
    }, 0);
  }

  function buildCollectionGwpEligibleGifts(activeSettings, cart, allGiftVariantIds, collectionCache) {
    const eligible = [];
    const giftIdSet = new Set(allGiftVariantIds);

    activeSettings.forEach((setting) => {
      (setting.collectionGwpConfigs || []).forEach((config) => {
        const cacheKey = getCollectionGwpCacheKey(config);
        const cache = collectionCache[cacheKey];
        if (!cache || !cache.loaded) return;

        const perConfigThreshold = config.threshold ?? setting.threshold;
        if (typeof perConfigThreshold === 'number' && setting.netSales < perConfigThreshold) {
          return;
        }

        const cartProductIds = (cart.items || [])
          .filter((item) => !giftIdSet.has(normalizeId(item.variant_id)))
          .map((item) => normalizeId(item.product_id))
          .filter(Boolean);

        const collectionProductIds = new Set(
          (cache.productIds || []).map((id) => normalizeId(id)).filter(Boolean)
        );

        const hasCollectionProduct = cartProductIds.some((id) => collectionProductIds.has(id));
        if (!hasCollectionProduct) return;

        const giftVariantId = cache.giftVariantId || config.fallbackGiftVariantId;
        if (!giftVariantId) return;

        eligible.push({
          giftVariantId: toGid(giftVariantId),
          discountCode: setting.appliedDiscountCode,
          type: 'collectionGwp',
        });
      });
    });

    return eligible;
  }

  function paidCartProductIds(cart, allGiftVariantIds) {
    const giftIdSet = new Set(allGiftVariantIds);
    return (cart.items || [])
      .filter((item) => item && !giftIdSet.has(normalizeId(item.variant_id)))
      .map((item) => normalizeId(item.product_id))
      .filter(Boolean);
  }

  function collectionProductIdSet(handle, collectionProducts) {
    return new Set(
      (collectionProducts?.[handle] || []).map((id) => normalizeId(id)).filter(Boolean)
    );
  }

  function checkProductIdUnion(check, collectionProducts) {
    const union = new Set();
    (check.collections || []).forEach((handle) => {
      collectionProductIdSet(handle, collectionProducts).forEach((id) => union.add(id));
    });
    (check.productIds || []).forEach((id) => {
      const key = normalizeId(id);
      if (key) union.add(key);
    });
    return union;
  }

  function cartMatchesCollectionCheck(check, paidProductIds, collectionProducts) {
    if (!paidProductIds.length) return false;
    if (!check.collections.length && !(check.productIds || []).length) return false;

    const membership = check.collections.map((handle) => {
      const ids = collectionProductIdSet(handle, collectionProducts);
      return paidProductIds.some((id) => ids.has(id));
    });

    if (check.match === 'all_present') {
      return membership.length > 0 && membership.every(Boolean);
    }

    const union = checkProductIdUnion(check, collectionProducts);
    if (!union.size) return false;

    if (check.match === 'all') {
      return paidProductIds.every((id) => union.has(id));
    }

    return paidProductIds.some((id) => union.has(id));
  }

  function buildCollectionChecksEligibleGifts(activeSettings, cart, allGiftVariantIds, collectionProducts) {
    const eligible = [];

    activeSettings.forEach((setting) => {
      const claimedAltIds = new Set();
      forEachMatchingCollectionCheck(
        setting,
        cart,
        allGiftVariantIds,
        collectionProducts,
        (check) => {
          const exclusive = check.exclusive
            ? exclusiveCollectionSelection(check, cart.items, setting.appliedDiscountCode)
            : null;
          const resolved = exclusive ? exclusive.choice.components : resolveCollectionCheckGifts(check, cart, claimedAltIds);
          resolved.forEach((gift) => {
            eligible.push({
              giftVariantId: toGid(gift.giftVariantId),
              discountCode: setting.appliedDiscountCode,
              type: 'collectionCheck',
              quantity: gift.quantity >= 1 ? gift.quantity : 1,
              requireUserChoice: collectionCheckRequiresUserChoice(setting, check) && !(exclusive && exclusive.selected),
            });
          });
        }
      );
    });

    return eligible;
  }

  async function ensureCollectionCaches(discountSettings, collectionCache, collectionProducts) {
    const configs = [];
    const seen = new Set();

    discountSettings.forEach((setting) => {
      (setting.collectionGwpConfigs || []).forEach((config) => {
        const key = getCollectionGwpCacheKey(config);
        if (seen.has(key) || collectionCache[key]?.loaded) return;
        seen.add(key);
        configs.push(config);
      });
    });

    await Promise.all(
      configs.map(async (config) => {
        const cacheKey = getCollectionGwpCacheKey(config);
        try {
          const response = await fetch(`/collections/${encodeURIComponent(config.handle)}/products.json?limit=250`);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = await response.json();
          collectionCache[cacheKey] = {
            loaded: true,
            handle: config.handle,
            productIds: (data.products || []).map((product) => product.id).filter(Boolean),
            giftVariantId: config.fallbackGiftVariantId || null,
            fallbackGiftVariantId: config.fallbackGiftVariantId || null,
            threshold: config.threshold ?? null,
          };
        } catch (error) {
          console.warn(LOG, 'Failed to load collection cache', config.handle, error);
          collectionCache[cacheKey] = { loaded: true, productIds: [], giftVariantId: config.fallbackGiftVariantId || null };
        }
      })
    );

    const missingHandles = collectionHandlesFromSettings(discountSettings).filter(
      (handle) => !Array.isArray(collectionProducts?.[handle])
    );

    await Promise.all(
      missingHandles.map(async (handle) => {
        try {
          const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          collectionProducts[handle] = (data.products || []).map((product) => product.id).filter(Boolean);
        } catch (error) {
          console.warn(LOG, 'Failed to load collection products', handle, error);
          collectionProducts[handle] = [];
        }
      })
    );
  }

  function evaluateCart(cart, config) {
    const {
      discountSettings,
      collectionCache,
      collectionProducts,
      requireSubscriptionForGwp,
      globalCouponCode,
    } = config;

    if (!discountSettings.length) {
      return { toRemove: [], toFixQty: [], toAdd: [], planKey: 'idle' };
    }

    const hasAtLeastOneSubscription = cartHasSubscription(cart);
    const allGiftVariantIds = getAllGiftVariantIds(discountSettings, collectionCache);
    const netSales = netMerchandiseSales(cart, allGiftVariantIds);
    const appliedDiscounts = getAppliedDiscounts(cart, resolveRuntimeGlobalCoupon(globalCouponCode));

    if (!cartHasPaidMerchandise(cart, allGiftVariantIds)) {
      return emptyPaidCartGwpPlan(cart, allGiftVariantIds);
    }

    const subscriptionRequiredForGwp = (setting) =>
      requireSubscriptionForGwp || setting?.needsAtLeastOneSubscription === true;

    const isGwpBlockedBySubscription = (sourceCode) => {
      if (hasAtLeastOneSubscription) return false;
      if (requireSubscriptionForGwp) return true;
      if (!sourceCode) return false;
      const setting = discountSettings.find(
        (s) => s.discountCode.toLowerCase() === String(sourceCode).toLowerCase()
      );
      return !!setting && subscriptionRequiredForGwp(setting);
    };

    let activeDiscountSettings = [];

    if (!(requireSubscriptionForGwp && !hasAtLeastOneSubscription)) {
      const seenSettingKeys = new Set();

      appliedDiscounts.forEach((applied) => {
        const appliedLower = applied.identifier.toLowerCase();
        const matchingSetting = discountSettings.find((s) => {
          const settingCode = s.discountCode.toLowerCase();
          if (applied.source === 'code') {
            return settingCode === appliedLower;
          }
          return appliedLower.includes(settingCode);
        });

        if (!matchingSetting) return;

        const dedupeKey = matchingSetting.discountCode.toLowerCase();
        if (seenSettingKeys.has(dedupeKey)) return;
        seenSettingKeys.add(dedupeKey);

        const hasDropdownGifts = matchingSetting.giftOptionsForSelectorDropdown.length > 0;
        const hasThresholdGifts = matchingSetting.giftOptionsForThreshold.length > 0;
        const hasCollectionGwp = (matchingSetting.collectionGwpConfigs || []).length > 0;

        if (
          hasDropdownGifts &&
          !hasThresholdGifts &&
          !hasCollectionGwp &&
          typeof matchingSetting.threshold === 'number' &&
          netSales < matchingSetting.threshold
        ) {
          return;
        }

        if (hasCollectionGwp) {
          const threshold = matchingSetting.threshold;
          if (typeof threshold === 'number' && netSales < threshold) {
            return;
          }
        }

        if (subscriptionRequiredForGwp(matchingSetting) && !hasAtLeastOneSubscription) {
          return;
        }

        activeDiscountSettings.push({
          ...matchingSetting,
          appliedDiscountCode: matchingSetting.discountCode,
          appliedDiscountTitle: applied.identifier,
          appliedDiscountSource: applied.source,
          netSales,
        });
      });
    }

    const singleGiftSettings = activeDiscountSettings.filter(
      (s) => !s.isMultiGift && s.hasDropdownGifts && getSelectorChoicePools(s).length === 0
    );

    const multiGiftSettings = activeDiscountSettings.filter(
      (s) => getSelectorChoicePools(s).length > 0
    );

    const thresholdEligibleGifts = [];
    activeDiscountSettings.forEach((setting) => {
      setting.giftOptionsForThreshold.forEach((thresholdGift) => {
        if (setting.netSales < thresholdGift.threshold) return;
        // Same-threshold alternatives are chosen via selector, not auto-stacked.
        if (isGiftInSelectorChoicePool(setting, thresholdGift.giftVariantId)) return;

        thresholdEligibleGifts.push({
          giftVariantId: toGid(thresholdGift.giftVariantId),
          threshold: thresholdGift.threshold,
          discountCode: setting.appliedDiscountCode,
        });
      });
    });

    const collectionGwpEligibleGifts = buildCollectionGwpEligibleGifts(
      activeDiscountSettings,
      cart,
      allGiftVariantIds,
      collectionCache
    );
    const collectionCheckEligibleGifts = buildCollectionChecksEligibleGifts(
      activeDiscountSettings,
      cart,
      allGiftVariantIds,
      collectionProducts || {}
    );
    const collectionCheckCodes = new Set(
      collectionCheckEligibleGifts.map((gift) => String(gift.discountCode || '').toLowerCase())
    );
    activeDiscountSettings.forEach((setting) => {
      if (setting.exclusiveCollectionChecks) collectionCheckCodes.add(String(setting.appliedDiscountCode).toLowerCase());
    });
    // Collection checks replace that code's $75 giftTiers. Disjoint checks may stack several gifts.
    for (let i = thresholdEligibleGifts.length - 1; i >= 0; i -= 1) {
      if (collectionCheckCodes.has(String(thresholdEligibleGifts[i].discountCode || '').toLowerCase())) {
        thresholdEligibleGifts.splice(i, 1);
      }
    }

    const sellingPlanEligibleGifts = [];
    const sellingPlanDecisions = [];
    const paidPlanLines = sellingPlanLines(cart, allGiftVariantIds);
    const activeCodes = new Set(
      activeDiscountSettings.map((setting) => setting.discountCode.toLowerCase())
    );

    discountSettings.forEach((setting) => {
      (setting.sellingPlanGifts || []).forEach((rule) => {
        const giftVariantId = toGid(rule.giftVariantId);
        const decision = {
          discountCode: setting.discountCode,
          giftVariantId,
          sellingPlanId: rule.sellingPlanId || null,
          sellingPlanNameSubstring: rule.sellingPlanNameSubstring || null,
          cartPlans: paidPlanLines,
        };

        if (!activeCodes.has(setting.discountCode.toLowerCase())) {
          sellingPlanDecisions.push({ ...decision, shouldAdd: false, reason: 'discount code not applied' });
          return;
        }

        const threshold = typeof rule.threshold === 'number' ? rule.threshold : 0;
        if (netSales < threshold) {
          sellingPlanDecisions.push({
            ...decision,
            shouldAdd: false,
            reason: `net $${netSales} is under threshold $${threshold}`,
          });
          return;
        }

        if (!cartMatchesSellingPlanGift(cart, rule, allGiftVariantIds)) {
          const reason = rule.sellingPlanId
            ? `no paid line with selling plan id ${rule.sellingPlanId}`
            : `no paid line whose selling plan name contains "${rule.sellingPlanNameSubstring}"`;
          sellingPlanDecisions.push({ ...decision, shouldAdd: false, reason });
          return;
        }

        sellingPlanDecisions.push({
          ...decision,
          shouldAdd: true,
          reason: 'plan matched',
          maxGiftQuantity: rule.maxGiftQuantity,
        });
        sellingPlanEligibleGifts.push({
          giftVariantId,
          discountCode: setting.discountCode,
          maxGiftQuantity: rule.maxGiftQuantity,
        });
      });
    });

    const eligibleThresholdGiftIds = thresholdEligibleGifts.map((g) => normalizeId(g.giftVariantId));
    const eligibleCollectionGiftIds = [
      ...collectionGwpEligibleGifts,
      ...collectionCheckEligibleGifts,
    ].map((g) => normalizeId(g.giftVariantId));
    const eligibleSellingPlanGiftIds = sellingPlanEligibleGifts.map((g) => normalizeId(g.giftVariantId));
    const allEligibleGiftIds = activeDiscountSettings.flatMap((s) =>
      s.giftOptionsForSelectorDropdown.map((g) => normalizeId(g.giftVariantId))
    );
    const appliedDiscountCodes = activeDiscountSettings.map((s) => s.appliedDiscountCode.toLowerCase());

    const giftLinesInCart = (cart.items || []).filter((item) =>
      allGiftVariantIds.includes(normalizeId(item.variant_id)) ||
      activeDiscountSettings.some((setting) => (setting.collectionChecks || []).some((check) => check.exclusive) &&
        isExclusiveGiftLine(item, setting.appliedDiscountCode))
    );

    const toRemove = [];
    const toFixQty = [];
    const toAdd = [];

    giftLinesInCart.forEach((giftLine) => {
      const props = getLineProps(giftLine);

      if (isProtectedNonGwpGift(giftLine)) {
        return;
      }

      const sourceCode = props._discount_code_source
        ? String(props._discount_code_source).toLowerCase()
        : '';

      if (isGwpBlockedBySubscription(sourceCode)) {
        toRemove.push({ key: giftLine.key, quantity: giftLine.quantity });
        return;
      }

      const variantId = normalizeId(giftLine.variant_id);
      const isEligibleDropdownGift = activeDiscountSettings.some((setting) =>
        setting.giftOptionsForSelectorDropdown.some(
          (g) => normalizeId(g.giftVariantId) === variantId
        )
      );
      const isEligibleThresholdGift = eligibleThresholdGiftIds.includes(variantId);
      const isEligibleCollectionGift = eligibleCollectionGiftIds.includes(variantId);
      const isEligibleSellingPlanGift = eligibleSellingPlanGiftIds.includes(variantId);
      const isEligibleUnderAnySetting =
        isEligibleDropdownGift ||
        isEligibleThresholdGift ||
        isEligibleCollectionGift ||
        isEligibleSellingPlanGift;

      let shouldRemove = false;

      if (sourceCode) {
        const sourceCodeStillActive = appliedDiscountCodes.includes(sourceCode);
        if (!sourceCodeStillActive) {
          if (!isEligibleUnderAnySetting) shouldRemove = true;
        } else if (!isEligibleUnderAnySetting) {
          const isCheckoutGwp = props._upsell_module === 'checkout_gwp';
          if (isCheckoutGwp) shouldRemove = true;
        }
      } else if (!isEligibleUnderAnySetting) {
        shouldRemove = true;
      }

      if (shouldRemove) {
        toRemove.push({ key: giftLine.key, quantity: giftLine.quantity });
      }
    });

    // Choice pools: remove locked gifts, then keep max ONE gift per same-threshold pool.
    multiGiftSettings.forEach((setting) => {
      const discountCode = setting.appliedDiscountCode;
      const choicePools = getSelectorChoicePools(setting);
      const activePools = getActiveSelectorChoicePools(setting);
      const activePoolVariantIds = new Set(
        activePools.flatMap((pool) =>
          pool.gifts.map((g) => normalizeId(g.giftVariantId))
        )
      );
      const allPoolVariantIds = new Set(
        choicePools.flatMap((pool) =>
          pool.gifts.map((g) => normalizeId(g.giftVariantId))
        )
      );

      const matchingLines = giftLinesInCart.filter((line) => {
        const variantId = normalizeId(line.variant_id);
        if (!allPoolVariantIds.has(variantId)) return false;
        if (toRemove.some((r) => r.key === line.key)) return false;
        const props = getLineProps(line);
        const sourceCode = props._discount_code_source
          ? String(props._discount_code_source).toLowerCase()
          : '';
        return !sourceCode || sourceCode === discountCode.toLowerCase();
      });

      matchingLines.forEach((line) => {
        const variantId = normalizeId(line.variant_id);
        if (!activePoolVariantIds.has(variantId)) {
          toRemove.push({ key: line.key, quantity: line.quantity });
        }
      });

      activePools.forEach((pool) => {
        const poolVariantIds = new Set(
          pool.gifts.map((g) => normalizeId(g.giftVariantId))
        );
        const poolLines = matchingLines.filter(
          (line) =>
            poolVariantIds.has(normalizeId(line.variant_id)) &&
            !toRemove.some((r) => r.key === line.key)
        );
        if (poolLines.length <= 1) return;

        const keepLine = poolLines[0];
        poolLines.forEach((line) => {
          if (line.key === keepLine.key) return;
          if (toRemove.some((r) => r.key === line.key)) return;
          toRemove.push({ key: line.key, quantity: line.quantity });
        });
      });
    });

    giftLinesInCart.forEach((giftLine) => {
      const variantId = normalizeId(giftLine.variant_id);
      const isEligibleDropdown = allEligibleGiftIds.includes(variantId);
      const isEligibleThreshold = eligibleThresholdGiftIds.includes(variantId);
      const isEligibleCollection = eligibleCollectionGiftIds.includes(variantId);
      const isEligibleSellingPlan = eligibleSellingPlanGiftIds.includes(variantId);
      if (!(isEligibleDropdown || isEligibleThreshold || isEligibleCollection || isEligibleSellingPlan)) return;

      const sellingPlanOnly =
        isEligibleSellingPlan && !isEligibleDropdown && !isEligibleThreshold && !isEligibleCollection;
      const targetQty = sellingPlanOnly
        ? maxSellingPlanGiftQuantity(sellingPlanEligibleGifts, variantId)
        : isEligibleCollection
          ? collectionCheckGiftQuantity(collectionCheckEligibleGifts, variantId)
          : 1;

      if (giftLine.quantity !== targetQty) {
        toFixQty.push({ key: giftLine.key, quantity: targetQty });
      }
    });

    const canAddGifts = !(requireSubscriptionForGwp && !hasAtLeastOneSubscription);
    const giftVariantInCart = (variantId) =>
      (cart.items || []).some((line) => normalizeId(line.variant_id) === normalizeId(variantId));

    const swapPools = [];
    activeDiscountSettings.forEach((setting) => {
      getActiveSelectorChoicePools(setting).forEach((pool) => {
        if (pool.gifts.length < 2) return;
        const selected =
          pool.gifts.find((gift) => giftVariantInCart(gift.giftVariantId)) || pool.gifts[0];
        swapPools.push({
          discountCode: setting.appliedDiscountCode,
          gifts: pool.gifts.map((gift) => ({
            giftVariantId: toGid(gift.giftVariantId),
            giftLabel: gift.giftLabel || 'Free Gift',
            unlocked: true,
          })),
          selectedVariantId: toGid(selected.giftVariantId),
        });
      });

      const claimedAltIds = new Set();
      forEachMatchingCollectionCheck(
        setting,
        cart,
        allGiftVariantIds,
        collectionProducts || {},
        (check) => {
          const pool = collectionCheckDisplayPool(
            check,
            cart,
            setting.appliedDiscountCode,
            claimedAltIds,
            collectionCheckRequiresUserChoice(setting, check)
          );
          if (pool) collectionCheckSlotPools(pool, cart).forEach((slot) => swapPools.push(slot));
        }
      );
    });

    if (canAddGifts && singleGiftSettings.length > 0) {
      singleGiftSettings.forEach((setting) => {
        if (collectionCheckCodes.has(String(setting.appliedDiscountCode || '').toLowerCase())) return;
        const giftVariant = setting.giftOptionsForSelectorDropdown[0].giftVariantId;
        const discountCode = setting.appliedDiscountCode;
        if (
          !giftVariantInCart(giftVariant) &&
          !toAdd.some((g) => normalizeId(g.variantId) === normalizeId(giftVariant))
        ) {
          toAdd.push({ variantId: giftVariant, discountCode, type: 'dropdown' });
        }
      });
    }

    if (canAddGifts && multiGiftSettings.length > 0) {
      multiGiftSettings.forEach((setting) => {
        if (collectionCheckCodes.has(String(setting.appliedDiscountCode || '').toLowerCase())) return;
        getActiveSelectorChoicePools(setting).forEach((pool) => {
          if (pool.gifts.some((gift) => giftVariantInCart(gift.giftVariantId))) return;
          const giftVariant = pool.gifts[0].giftVariantId;
          if (toAdd.some((gift) => normalizeId(gift.variantId) === normalizeId(giftVariant))) return;
          toAdd.push({
            variantId: giftVariant,
            discountCode: setting.appliedDiscountCode,
            type: 'dropdown',
          });
        });
      });
    }

    if (canAddGifts && thresholdEligibleGifts.length > 0) {
      thresholdEligibleGifts.forEach((thresholdGift) => {
        const giftVariant = thresholdGift.giftVariantId;
        if (
          !giftVariantInCart(giftVariant) &&
          !toAdd.some((g) => normalizeId(g.variantId) === normalizeId(giftVariant))
        ) {
          toAdd.push({
            variantId: giftVariant,
            discountCode: thresholdGift.discountCode,
            type: 'threshold',
            threshold: thresholdGift.threshold,
          });
        }
      });
    }

    if (canAddGifts && collectionGwpEligibleGifts.length > 0) {
      collectionGwpEligibleGifts.forEach((collectionGift) => {
        const giftVariant = collectionGift.giftVariantId;
        if (
          !giftVariantInCart(giftVariant) &&
          !toAdd.some((g) => normalizeId(g.variantId) === normalizeId(giftVariant))
        ) {
          toAdd.push({
            variantId: giftVariant,
            discountCode: collectionGift.discountCode,
            type: 'collectionGwp',
          });
        }
      });
    }

    if (canAddGifts && collectionCheckEligibleGifts.length > 0) {
      collectionCheckEligibleGifts.forEach((collectionGift) => {
        if (collectionGift.requireUserChoice) return;
        const giftVariant = collectionGift.giftVariantId;
        if (
          !giftVariantInCart(giftVariant) &&
          !toAdd.some((g) => normalizeId(g.variantId) === normalizeId(giftVariant))
        ) {
          toAdd.push({
            variantId: giftVariant,
            discountCode: collectionGift.discountCode,
            type: 'collectionCheck',
            quantity: collectionGift.quantity >= 1 ? collectionGift.quantity : 1,
          });
        }
      });
    }

    if (canAddGifts && sellingPlanEligibleGifts.length > 0) {
      sellingPlanEligibleGifts.forEach((planGift) => {
        const giftVariant = planGift.giftVariantId;
        const decision = sellingPlanDecisions.find(
          (entry) =>
            entry.shouldAdd &&
            normalizeId(entry.giftVariantId) === normalizeId(giftVariant) &&
            entry.discountCode === planGift.discountCode
        );
        if (!decision) return;

        if (giftVariantInCart(giftVariant)) {
          decision.shouldAdd = false;
          decision.reason = 'already in cart';
          return;
        }

        if (toAdd.some((gift) => normalizeId(gift.variantId) === normalizeId(giftVariant))) {
          decision.shouldAdd = false;
          decision.reason = 'already queued by another rule';
          return;
        }

        decision.reason = 'will add';
        decision.maxGiftQuantity = maxSellingPlanGiftQuantity(
          sellingPlanEligibleGifts,
          giftVariant
        );
        toAdd.push({
          variantId: giftVariant,
          discountCode: planGift.discountCode,
          type: 'sellingPlanGwp',
        });
      });
    } else if (!canAddGifts) {
      sellingPlanDecisions.forEach((decision) => {
        if (!decision.shouldAdd) return;
        decision.shouldAdd = false;
        decision.reason = 'subscription required and cart has none';
      });
    }

    const planKey = JSON.stringify({
      r: toRemove.map((x) => x.key).sort(),
      f: toFixQty.map((x) => `${x.key}:${x.quantity}`).sort(),
      a: toAdd.map((x) => `${normalizeId(x.variantId)}:${x.discountCode}:${x.quantity || 1}`).sort(),
    });

    return { toRemove, toFixQty, toAdd, planKey, netSales, swapPools,
      exclusiveCollectionChecks: activeDiscountSettings.some((setting) => setting.exclusiveCollectionChecks) };
  }

  let configCache = null;
  let lastIdlePlanKey = true;
  let lastSyncPlanKey = '';
  let lastSyncVersion = null;
  let syncResent = false;
  let pendingAddKey = '';
  let debounceTimer = null;
  let inFlight = false;
  let skipSyncFor = null;
  let ruleCorrections = 0;
  let ruleRuns = 0;
  let ruleWrites = 0;
  let ruleWritesFrom = 0;
  let giftWrites = 0;
  let giftWriteRunning = null;
  let giftAddRunning = false;
  let giftAddBase = null;
  let resyncs = 0;
  let passEvaluated = [];
  let checkoutAt = 0;
  const droppedWhileInFlight = new Set();
  const idleWaiters = [];
  const QUANTITY_WAIT = 8000;
  const QUANTITY_HOLD = 200;
  const CHECKOUT_HOLD = 10000;
  const GIFT_WRITE_HEADER = 'X-FD-Gift-Write';

  function isPending() {
    return inFlight || Boolean(debounceTimer) || resyncs > 0;
  }

  function notifyIdle() {
    if (isPending()) return;
    idleWaiters.splice(0).forEach((resolve) => resolve());
  }

  // Resolves once no sync pass is running or waiting to add, or after maxWait ms.
  function whenIdle(maxWait) {
    if (!isPending()) return Promise.resolve();
    return new Promise((resolve) => {
      idleWaiters.push(resolve);
      if (maxWait > 0) setTimeout(resolve, maxWait);
    });
  }

  function cartVersionOf(cart) {
    const store = window.FDCart;
    return store && typeof store.cartVersion === 'function' ? store.cartVersion(cart) : null;
  }

  function writesStarted() {
    const store = window.FDCart;
    return store && typeof store.getWriteState === 'function' ? store.getWriteState().started : 0;
  }

  // Counts each gift write, so a pass can tell its own writes from others in the store's count.
  function sendGiftWrite(url, payload, base) {
    giftWrites += 1;
    const startedBefore = writesStarted();
    const request = fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', [GIFT_WRITE_HEADER]: '1' },
      body: JSON.stringify(payload),
    });
    const answered = request.then(() => {}, () => {});
    giftWriteRunning = answered;
    giftAddRunning = /\/cart\/add/.test(url);
    giftAddBase = giftAddRunning && base ? { cart: base, started: startedBefore } : null;
    answered.then(() => {
      if (giftWriteRunning !== answered) return;
      giftWriteRunning = null;
      giftAddRunning = false;
      giftAddBase = null;
    });
    return request;
  }

  // The cart a gift add on its way was planned on, while no other write or outside change came since: the add keeps its attributes and codes.
  function cartBeforeGiftAdd() {
    const store = window.FDCart;
    const state = giftAddBase && store && typeof store.getWriteState === 'function' ? store.getWriteState() : null;
    if (!state || state.pending > 1 || state.started > giftAddBase.started + 1 || state.outside) return null;
    return giftAddBase.cart;
  }

  // Resolves once the gift write on its way has answered, or after maxWait ms; null when none is on its way.
  function whenGiftWriteDone(maxWait) {
    if (!giftWriteRunning) return null;
    return Promise.race([giftWriteRunning, new Promise((resolve) => setTimeout(resolve, maxWait))]);
  }

  // Gift adds not sent yet are dropped while checkout opens: the checkout extension adds earned gifts.
  function holdAddsForCheckout() {
    checkoutAt = Date.now();
  }

  function checkoutStarted() {
    return Date.now() - checkoutAt < CHECKOUT_HOLD;
  }

  // Checkout did not open (back from the browser cache, or no navigation): gift adds resume on a fresh read.
  function resumeAfterCheckout() {
    if (!checkoutAt) return;
    checkoutAt = 0;
    readCart().then((cart) => sync(cart, { source: 'checkout-resume' }), () => {});
  }

  function quantityWritesPending() {
    const drawer = window.FDCartDrawerWrites;
    return Boolean(drawer && typeof drawer.quantityPending === 'function' && drawer.quantityPending());
  }

  // Ms left until a gift write may follow the drawer's quantity taps (quiet since the last tap or answer).
  function quantityHoldLeft() {
    const drawer = window.FDCartDrawerWrites;
    const quietSince = drawer && typeof drawer.lastQuantityActivity === 'function' ? drawer.lastQuantityActivity() : 0;
    return quietSince + QUANTITY_HOLD - Date.now();
  }

  // A page add (PDP, Build Box) still writing and painting the cart.
  function pageFlowRunning() {
    const hold = window.__fdCartRenderHold;
    const pageAdd = window.FDCartPageAdd;
    return Boolean((hold && hold.until > Date.now()) || (pageAdd && typeof pageAdd.current === 'function' && pageAdd.current()));
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // The newest write's own answer while the store still counts that write as running, with nothing else since.
  function answeredNewestWrite(cart) {
    const store = window.FDCart;
    if (!cart || !store || typeof store.getWriteState !== 'function') return false;
    const state = store.getWriteState();
    return state.started > 0 && state.pending === 1 && !state.overlapped && !state.outside &&
      cartVersionOf(cart) === state.started;
  }

  // Newest server cart once queued quantity taps (8s), page adds and cart writes (3s) end; null while taps still run.
  async function settledCart(cart) {
    const store = window.FDCart;
    const deadline = Date.now() + QUANTITY_WAIT;
    for (;;) {
      if (quantityWritesPending()) {
        const left = deadline - Date.now();
        if (left <= 0) return null;
        await window.FDCartDrawerWrites.whenQuantityIdle(left);
        continue;
      }
      if (pageFlowRunning() && Date.now() < deadline) {
        await wait(50);
        continue;
      }
      if (answeredNewestWrite(cart)) return cart;
      if (store && typeof store.whenWritesIdle === 'function') await store.whenWritesIdle(3000);
      if (quantityWritesPending()) continue;
      // Writes that ran side by side can land in either order, so neither one's cart is the newest: read.
      const state = store && typeof store.getWriteState === 'function' ? store.getWriteState() : null;
      const trusted = !(state && state.overlapped);
      if (trusted && store && typeof store.isCartCurrent === 'function' && store.isCartCurrent(cart)) return cart;
      const current = trusted && store && typeof store.getCurrentCart === 'function' ? store.getCurrentCart() : null;
      if (current) return current;
      const started = writesStarted();
      let read;
      try {
        read = await readCart();
      } catch (readError) {
        return cart;
      }
      if (writesStarted() === started && !quantityWritesPending()) return read;
      if (Date.now() >= deadline) return null;
    }
  }

  function getConfig() {
    if (!configCache) {
      configCache = loadConfig();
    }
    return configCache;
  }

  // Gift removals and quantity fixes go out in one write.
  async function updateGiftLines(toRemove, toFixQty) {
    if (!toRemove.length && !toFixQty.length) return null;

    const updates = {};
    toRemove.forEach(({ key }) => {
      updates[key] = 0;
    });
    toFixQty.forEach(({ key, quantity }) => {
      if (!(key in updates)) updates[key] = quantity;
    });

    const response = await sendGiftWrite('/cart/update.js', { updates });

    if (!response.ok) {
      throw new Error(`Gift update failed (${response.status})`);
    }

    return response.json();
  }

  async function addGiftLines(toAdd, base) {
    if (!toAdd.length) return null;

    const items = toAdd.map(({ variantId, discountCode, quantity, type }) => {
      const properties = {
        _upsell_module: 'checkout_gwp',
        _discount_code_source: discountCode,
      };
      if (type === 'collectionCheck') {
        properties[COLLECTION_GWP_ATTRIBUTE_KEY] = COLLECTION_GWP_ATTRIBUTE_VALUE;
      }
      return {
        id: Number(normalizeId(variantId)),
        quantity: quantity >= 1 ? quantity : 1,
        properties,
      };
    });

    const response = await sendGiftWrite('/cart/add.js', { items }, base);

    if (!response.ok) {
      throw new Error(`Gift add failed (${response.status})`);
    }

    await response.json();

    // A quantity tap on its way returns the newer cart, and the drawer keeps its look until then.
    if (quantityWritesPending()) return null;
    return readCart();
  }

  function cartHasCollectionGwpGate(cart) {
    const attrs = cart && cart.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
    return String(attrs[COLLECTION_GWP_ATTRIBUTE_KEY] || '') === COLLECTION_GWP_ATTRIBUTE_VALUE;
  }

  async function stampCollectionGwpGate(cart) {
    if (!cart || cartHasCollectionGwpGate(cart)) return cart;
    const next = {};
    const attrs = cart.attributes || {};
    Object.keys(attrs).forEach((key) => {
      next[key] = attrs[key];
    });
    next[COLLECTION_GWP_ATTRIBUTE_KEY] = COLLECTION_GWP_ATTRIBUTE_VALUE;
    const payload = { attributes: next };
    if (window.FDCartApi && typeof window.FDCartApi.updateCart === 'function') {
      try {
        return await window.FDCartApi.updateCart(payload);
      } catch (error) {
        console.warn(LOG, 'Failed to stamp', COLLECTION_GWP_ATTRIBUTE_KEY, error);
        return cart;
      }
    }
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      console.warn(LOG, 'Failed to stamp', COLLECTION_GWP_ATTRIBUTE_KEY, response.status);
      return cart;
    }
    return response.json();
  }

  function readCart() {
    if (window.FDCartApi && typeof window.FDCartApi.getCart === 'function') {
      return window.FDCartApi.getCart(true);
    }
    return fetch('/cart.js').then((res) => {
      if (!res.ok) throw new Error(`Cart read failed (${res.status})`);
      return res.json();
    });
  }

  // Gift lines render from product data the page may not have: warm it for the
  // gifts the cart's codes can add, before the threshold is crossed.
  function warmGiftsFor(cart, config) {
    if (typeof window.FDCartWarmGifts !== 'function' || !cart || !Array.isArray(cart.items) || !cart.items.length) return;
    const applied = getAppliedDiscounts(cart, resolveRuntimeGlobalCoupon(config.globalCouponCode))
      .map((entry) => ({ code: entry.identifier.toLowerCase(), exact: entry.source === 'code' }));
    const settings = config.discountSettings.filter((setting) => {
      const code = setting.discountCode.toLowerCase();
      return applied.some((entry) => (entry.exact ? entry.code === code : entry.code.includes(code)));
    });
    if (!settings.length) return;
    const inCart = new Set(cart.items.map((line) => normalizeId(line.variant_id)));
    const ids = getAllGiftVariantIds(settings, config.collectionCache).filter((id) => !inCart.has(id));
    if (ids.length) window.FDCartWarmGifts(ids);
  }

  function giftFixKey(plan) {
    return JSON.stringify({
      r: plan.toRemove.map((x) => x.key).sort(),
      f: plan.toFixQty.map((x) => `${x.key}:${x.quantity}`).sort(),
    });
  }

  // Waits while drawer quantity taps are on their way or quiet for less than QUANTITY_HOLD ms, so a quick tap takes the gift write back.
  async function holdForQuantityTaps(deadline) {
    while (!checkoutStarted() && Date.now() < deadline) {
      if (quantityWritesPending()) {
        await window.FDCartDrawerWrites.whenQuantityIdle(deadline - Date.now());
        continue;
      }
      const left = quantityHoldLeft();
      if (left <= 0) return;
      await wait(left);
    }
  }

  // The settled cart once drawer quantity taps are quiet (8s at most); null while taps still run, or for an add once checkout started.
  async function settledAfterTaps(cart, isAdd) {
    const deadline = Date.now() + QUANTITY_WAIT;
    let fresh = null;
    do {
      await holdForQuantityTaps(deadline);
      if (isAdd && checkoutStarted()) return null;
      fresh = await settledCart(cart);
    } while (fresh && !checkoutStarted() && quantityHoldLeft() > 0 && Date.now() < deadline);
    return fresh;
  }

  async function applyPlan(plan, cart, debounceDelay) {
    const { toRemove, toFixQty, toAdd } = plan;
    const isIdle = !toRemove.length && !toFixQty.length && !toAdd.length;

    if (isIdle) {
      lastIdlePlanKey = true;
      lastSyncPlanKey = '';
      lastSyncVersion = null;
      syncResent = false;
      pendingAddKey = '';
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      return null;
    }

    lastIdlePlanKey = false;
    let workingCart = cart;
    let wrote = false;
    let adds = toAdd;

    const syncKey = giftFixKey(plan);

    // A write that ended after the last fix and still needs it overwrote it: send it once more.
    const version = cartVersionOf(cart);
    const overwritten = syncKey === lastSyncPlanKey && !syncResent &&
      lastSyncVersion !== null && version !== null && version > lastSyncVersion;

    if (syncKey !== lastSyncPlanKey || overwritten) {
      syncResent = overwritten;
      lastSyncPlanKey = syncKey;
      lastSyncVersion = null;

      if (toRemove.length || toFixQty.length) {
        // Plan again on the settled cart: a quantity tap still on its way can make the fix wrong.
        const fresh = await settledAfterTaps(cart, false);
        if (!fresh) {
          lastSyncPlanKey = '';
          return null;
        }
        passEvaluated.push(fresh);
        const freshPlan = fresh === cart ? plan : evaluateCart(fresh, getConfig());
        workingCart = fresh;
        adds = freshPlan.toAdd;
        if (freshPlan.toRemove.length || freshPlan.toFixQty.length) {
          const freshKey = giftFixKey(freshPlan);
          if (freshKey !== syncKey) {
            lastSyncPlanKey = freshKey;
            syncResent = false;
          }
          workingCart = await updateGiftLines(freshPlan.toRemove, freshPlan.toFixQty);
          wrote = true;
          lastSyncVersion = cartVersionOf(workingCart);
        } else {
          lastSyncPlanKey = '';
        }
      }
    }

    if (!adds.length) {
      return wrote ? workingCart : null;
    }

    const addKey = JSON.stringify(
      adds.map((a) => `${normalizeId(a.variantId)}:${a.discountCode}:${a.quantity || 1}`).sort()
    );

    if (addKey === pendingAddKey && debounceTimer) {
      return wrote ? workingCart : null;
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    pendingAddKey = addKey;
    const store = window.FDCart;
    // The debounce runs from the newest cart write's start, so it overlaps that write; once spent, add at once.
    const writeState = store && typeof store.getWriteState === 'function' ? store.getWriteState() : null;
    const sinceWrite = writeState && writeState.startedAt ? Date.now() - writeState.startedAt : 0;
    const delay = Math.max(0, debounceDelay - sinceWrite);

    return new Promise((resolve) => {
      const addGifts = async () => {
        debounceTimer = null;
        pendingAddKey = '';

        try {
          // A quantity tap or a write still on its way decides whether the gift is earned.
          const freshCart = await settledAfterTaps(workingCart, true);
          if (!freshCart) {
            resolve(wrote ? workingCart : null);
            return;
          }
          passEvaluated.push(freshCart);
          const freshConfig = getConfig();
          const freshPlan = evaluateCart(freshCart, freshConfig);

          const giftsToAdd = freshPlan.toAdd.filter((gift) => {
            const variantId = normalizeId(gift.variantId);
            const inCart = (freshCart.items || []).some(
              (line) => normalizeId(line.variant_id) === variantId
            );
            return !inCart;
          });

          const sellingPlanQueued = freshPlan.toAdd.filter((gift) => gift.type === 'sellingPlanGwp');
          const sellingPlanAdding = giftsToAdd.filter((gift) => gift.type === 'sellingPlanGwp');
          const sellingPlanSkipped = sellingPlanQueued.filter(
            (gift) => !sellingPlanAdding.some((adding) => normalizeId(adding.variantId) === normalizeId(gift.variantId))
          );

          if (sellingPlanQueued.length || sellingPlanAdding.length) {
            console.log(LOG, 'selling plan add', {
              adding: sellingPlanAdding,
              notAdded: sellingPlanSkipped.map((gift) => ({
                ...gift,
                reason: 'already in cart before add',
              })),
            });
          }

          // Checkout started: the checkout extension adds earned gifts.
          if (!giftsToAdd.length || checkoutStarted()) {
            resolve(wrote ? workingCart : null);
            return;
          }

          console.log(LOG, 'Adding gifts', giftsToAdd);
          const updatedCart = await addGiftLines(giftsToAdd, freshCart);
          resolve(updatedCart);
        } catch (error) {
          console.error(LOG, 'Failed to add gifts', error);
          resolve(wrote ? workingCart : null);
        }
      };
      if (delay > 0) debounceTimer = setTimeout(addGifts, delay);
      else addGifts();
    });
  }

  async function sync(cart, options) {
    options = options || {};
    if (exclusiveSwapActive && options.source !== 'swapGift') return null;

    if (skipSyncFor) {
      // Only the refreshed cart itself is skipped: a cart the refresh's own write returned still counts.
      const refreshed = skipSyncFor === cart;
      skipSyncFor = null;
      if (refreshed) return null;
    }

    if (inFlight) {
      // A drawer gift rule (no-subscription removal, orphan welcome gifts) undoing this pass wins.
      if (!ruleCorrections) droppedWhileInFlight.add(cart);
      return null;
    }

    const config = getConfig();
    if (!config.discountSettings.length) {
      return null;
    }

    inFlight = true;
    let updatedCart = null;
    let painted = null;
    const writesAtStart = writesStarted();
    const giftWritesAtStart = giftWrites;
    const ruleWritesAtStart = ruleWrites;
    const ruleRunsAtStart = ruleRuns;
    passEvaluated = [cart];

    try {
      await ensureCollectionCaches(
        config.discountSettings,
        config.collectionCache,
        config.collectionProducts || {}
      );

      const gatedCart = await stampCollectionGwpGate(cart);
      warmGiftsFor(gatedCart, config);
      const plan = evaluateCart(gatedCart, config);
      const isIdle = !plan.toRemove.length && !plan.toFixQty.length && !plan.toAdd.length;

      if (isIdle) {
        lastIdlePlanKey = true;
        return null;
      }

      if (plan.planKey === lastSyncPlanKey && plan.toAdd.length && debounceTimer) {
        return null;
      }

      updatedCart = await applyPlan(plan, cart, config.debounceDelay);

      if (updatedCart && typeof window.__fdCartGwpRefresh === 'function') {
        skipSyncFor = updatedCart;
        await window.__fdCartGwpRefresh(updatedCart, options.renderOptions || {});
        painted = (window.FDCart && window.FDCart.getCart()) || updatedCart;
        if (typeof window.updateCartCount === 'function') {
          window.updateCartCount(updatedCart.item_count);
        }
      }

      return updatedCart || null;
    } catch (error) {
      console.error(LOG, 'Sync failed', error);
      return null;
    } finally {
      inFlight = false;
      skipSyncFor = null;
      // A shopper's or an app's write that ran beside this pass can settle into a cart no pass evaluates.
      const othersWrote = writesStarted() - writesAtStart >
        (giftWrites - giftWritesAtStart) + (ruleWrites - ruleWritesAtStart);
      // The cart its own gift write returned counts as seen only when no other write ran beside the pass.
      const seen = passEvaluated.concat(othersWrote || !updatedCart ? [] : [updatedCart]).map(evaluatedKey);
      const missed = [...droppedWhileInFlight].some((dropped) => dropped && !seen.includes(evaluatedKey(dropped)));
      droppedWhileInFlight.clear();
      const raced = ruleRuns === ruleRunsAtStart && othersWrote;
      if (missed || raced) resyncNewest(painted);
      notifyIdle();
    }
  }

  function paintKey(cart) {
    const store = window.FDCart;
    const lines = store && typeof store.fingerprintComposition === 'function'
      ? store.fingerprintComposition(cart)
      : JSON.stringify((cart.items || []).map((line) => [line.key, line.quantity]));
    return lines + '#' + cart.total_price;
  }

  function evaluatedKey(cart) {
    const codes = (cart.discount_codes || []).map((entry) => `${entry.code}:${entry.applicable}`).sort().join(',');
    return paintKey(cart) + '#' + codes;
  }

  // Evaluates the newest cart once writes end, and repaints it when the drawer shows another one.
  async function resyncNewest(painted) {
    const drawer = window.FDCartDrawerWrites;
    resyncs += 1;
    try {
      const newest = await settledCart(null);
      if (!newest || !Array.isArray(newest.items)) return;
      const shown = (drawer && typeof drawer.paintedCart === 'function' && drawer.paintedCart()) || painted;
      if (shown && newest !== shown && paintKey(newest) !== paintKey(shown) &&
          typeof window.__fdCartGwpRefresh === 'function') {
        await window.__fdCartGwpRefresh(newest, {});
      }
      await sync(newest, { source: 'resync' });
    } catch (error) {
      console.error(LOG, 'Resync failed', error);
    } finally {
      resyncs -= 1;
      notifyIdle();
    }
  }

  async function duringRuleCorrection(run) {
    if (!ruleCorrections) ruleWritesFrom = writesStarted();
    ruleCorrections += 1;
    ruleRuns += 1;
    try {
      return await run();
    } finally {
      ruleCorrections -= 1;
      if (!ruleCorrections) ruleWrites += writesStarted() - ruleWritesFrom;
    }
  }

  // Gifts the cart earns and does not hold yet; none once checkout started (the checkout extension adds them).
  function plannedAdds(cart) {
    if (!cart || !Array.isArray(cart.items) || checkoutStarted()) return [];
    const config = getConfig();
    if (!config.discountSettings.length) return [];
    return evaluateCart(cart, config).toAdd;
  }

  // Keys of the gift lines the cart no longer earns, which the gift sync removes.
  function plannedRemovals(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    const config = getConfig();
    if (!config.discountSettings.length) return [];
    return evaluateCart(cart, config).toRemove.map((line) => line.key);
  }

  function getSwapPools(cart) {
    if (!cart) return [];
    const config = getConfig();
    return evaluateCart(cart, config).swapPools || [];
  }

  function findSwapPool(pools, toVariantId, poolKey) {
    const targetId = normalizeId(toVariantId);
    if (poolKey) {
      const exact = (pools || []).find((entry) => entry.key === poolKey);
      if (exact) return exact;
    }
    return (pools || []).find((entry) => {
      const choices = entry.swapChoices && entry.swapChoices.length ? entry.swapChoices : entry.gifts;
      return (choices || []).some((gift) => normalizeId(gift.giftVariantId) === targetId);
    }) || null;
  }

  let exclusiveSwapActive = false;

  async function swapGift(toVariantId, poolKey, fromVariantId) {
    if (exclusiveSwapActive) return null;
    const targetId = normalizeId(toVariantId);
    if (!targetId) return null;

    let cart = await readCart();
    let pool = findSwapPool(getSwapPools(cart), targetId, poolKey);
    if (!pool) return null;
    if (pool.exclusive && isPending()) {
      await whenIdle(3000);
      if (isPending() || exclusiveSwapActive) return null;
      cart = await readCart();
      pool = findSwapPool(getSwapPools(cart), targetId, poolKey);
      if (!pool || !pool.exclusive) return null;
    }

    const fromId = normalizeId(fromVariantId) || normalizeId(pool.selectedVariantId);
    if (fromId && fromId === targetId && !pool.exclusive) return cart;
    const components = pool.exclusive ? collectionChoiceComponents(pool, targetId) : [];
    if (pool.exclusive && !components.length) return cart;
    if (!pool.exclusive && cartHasGiftVariant(cart, targetId)) return cart;
    const componentIds = new Set(components.map((gift) => normalizeId(gift.giftVariantId)));
    // Never take over paid or protected lines that happen to use the same variant.
    if (pool.exclusive && (cart.items || []).some((line) =>
      componentIds.has(normalizeId(line.variant_id)) && !isExclusiveGiftLine(line, pool.discountCode)
    )) return cart;
    const poolIds = new Set((pool.swapChoices || []).flatMap((choice) =>
      (choice.components || [choice]).map((gift) => normalizeId(gift.giftVariantId))
    ));
    const exclusiveRemovals = pool.exclusive ? (cart.items || []).filter((line) =>
      poolIds.has(normalizeId(line.variant_id)) && isExclusiveGiftLine(line, pool.discountCode)
    ) : [];
    const toRemove = pool.exclusive
      ? exclusiveRemovals.map((line) => ({ key: line.key, quantity: line.quantity }))
      : fromId
      ? (cart.items || [])
        .filter((line) => normalizeId(line.variant_id) === fromId)
        .map((line) => ({ key: line.key, quantity: line.quantity }))
      : [];
    if (fromId && !toRemove.length && !pool.requireUserChoice) return cart;

    const toAdd = pool.exclusive ? components.map((gift) => ({
      variantId: normalizeId(gift.giftVariantId),
      discountCode: pool.discountCode,
      quantity: gift.quantity,
      type: 'collectionCheck',
    })) : [{
      variantId: targetId,
      discountCode: pool.discountCode,
      quantity: quantityForPoolGift(pool, targetId),
      type: pool.type || 'collectionCheck',
    }];

    if (pool.exclusive) {
      if (exclusiveSwapActive) return null;
      exclusiveSwapActive = true;
    }
    return duringRuleCorrection(async () => {
      if (toRemove.length) {
        await updateGiftLines(toRemove, []);
      }
      let nextCart;
      try {
        nextCart = toAdd.length ? await addGiftLines(toAdd) : await readCart();
      } catch (error) {
        // A rejected bundle add must not silently discard the previous choice.
        if (pool.exclusive) {
          const partialCart = await readCart();
          const partialLines = (partialCart.items || []).filter((line) =>
            componentIds.has(normalizeId(line.variant_id)) && isExclusiveGiftLine(line, pool.discountCode));
          if (partialLines.length) await updateGiftLines(partialLines, []);
          if (exclusiveRemovals.length) await addGiftLines(exclusiveRemovals.map((line) => ({
            variantId: String(line.variant_id), quantity: line.quantity,
            discountCode: pool.discountCode, type: 'collectionCheck',
          })));
        }
        throw error;
      }
      if (nextCart) {
        await sync(nextCart, { source: 'swapGift' });
        nextCart = (window.FDCart && typeof window.FDCart.getCurrentCart === 'function'
          && window.FDCart.getCurrentCart()) || await readCart();
      }
      if (typeof window.__fdCartGwpRefresh === 'function' && nextCart) {
        skipSyncFor = nextCart;
        try {
          await window.__fdCartGwpRefresh(nextCart, {});
        } finally {
          skipSyncFor = null;
        }
      }
      return nextCart;
    }).finally(() => {
      if (pool.exclusive) exclusiveSwapActive = false;
    });
  }

  window.FDCartGwp = {
    sync,
    evaluateCart,
    plannedAdds,
    plannedRemovals,
    swapGift,
    getSwapPools,
    hasExclusiveCollectionChecks: function (cart) {
      return !!(cart && evaluateCart(cart, getConfig()).exclusiveCollectionChecks);
    },
    duringRuleCorrection,
    reloadConfig: function () {
      configCache = null;
      return getConfig();
    },
    isCheckoutGwpLine: function (item) {
      const props = getLineProps(item);
      return props._upsell_module === 'checkout_gwp';
    },
    isProtectedNonGwpGift: isProtectedNonGwpGift,
    isPending,
    whenIdle,
    whenGiftWriteDone,
    giftAddPending: () => giftAddRunning,
    cartBeforeGiftAdd,
    settledCart,
    holdAddsForCheckout,
    resumeAfterCheckout,
  };

  if (window.FDCart && typeof window.FDCart.subscribe === 'function') {
    window.FDCart.subscribe(function (cart) {
      if (cart) {
        sync(cart, { source: 'FDCart.subscribe' });
      }
    });
  }

})();
